import React, { useEffect, useState } from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFDownloadLink,
  pdf,
} from '@react-pdf/renderer';
import axios from 'axios';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import InvoiceDetailsPdf from '../Agency/Invoices/InvoiceDetailsPdf';
import { useGetInvoiceDetailsRevampQuery } from '@/graphql';
import { useLocation } from 'react-router-dom';
import LoadingState from '@/components/LoadingState';

// Create styles for PDF
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 30,
  },
  section: {
    margin: 10,
    padding: 10,
    flexGrow: 1,
  },
  heading: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
    textAlign: 'center',
  },
  content: {
    fontSize: 14,
    marginBottom: 10,
  },
});

// Simple Document Component as fallback
const MyDocument = () => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.section}>
        <Text style={styles.heading}>PDF Generation</Text>
        <Text style={styles.content}>hello world</Text>
      </View>
    </Page>
  </Document>
);

interface UploadUrlResponse {
  key: string;
  url: string;
  bucket: string;
  content_type: string;
  is_private: boolean;
}

const PDFGeneration: React.FC = () => {
  // Use useLocation to get the query parameters
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const invoiceId = queryParams.get('invoiceId');
  const accessToken = queryParams.get('pdfKey');

  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<{
    success?: boolean;
    message?: string;
  }>({});
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [uploadDetails, setUploadDetails] = useState<UploadUrlResponse | null>(
    null,
  );
  // const { currentAgency } = useAuth();
  const [currentAgency, setCurrentAgency] = useState<any>(null);
  const isPublicAccess = !!accessToken && !currentAgency;

  const {
    loading: loadingInvoice,
    error: invoiceError,
    data: invoiceData,
  } = useGetInvoiceDetailsRevampQuery({
    variables: {
      invoiceId: invoiceId || '',
    },
    skip: !invoiceId,
    context: accessToken
      ? {
          headers: {
            'X-Invoice-Pdf-Key': accessToken || '',
          },
        }
      : undefined,
  });

  useEffect(() => {
    if (invoiceId) {
      getCustomerAgency().then((agency) => {
        setCurrentAgency({
          name: agency.name,
          email: agency.email,
          phoneNumber: agency.phone_number,
          address: {
            addressLine1: agency.address.address_line1,
            addressLine2: agency.address.address_line2,
            city: agency.address.city,
            state: agency.address.state,
            zip: agency.address.zip,
          },
        });
      });
    }
  }, [invoiceId]);

  useEffect(() => {
    if (
      invoiceData?.listStripeInvoiceDetail?.invoice &&
      !loadingInvoice &&
      currentAgency
    ) {
      generatePDF();
    }
  }, [invoiceData, loadingInvoice, currentAgency]);

  const generatePDF = async () => {
    if (!invoiceData?.listStripeInvoiceDetail?.invoice) {
      return;
    }

    try {
      setIsGenerating(true);
      // Create PDF blob using InvoiceDetailsPdf component with real data
      const invoicePdf = (
        <InvoiceDetailsPdf
          invoice={invoiceData.listStripeInvoiceDetail.invoice}
          invoiceItems={invoiceData.listStripeInvoiceDetail.invoiceItems}
          currentAgency={currentAgency}
          refiningInvoiceNumber={false}
        />
      );

      const blob = await pdf(invoicePdf).toBlob();
      setPdfBlob(blob);

      // Now attempt to upload
      await getUploadUrlAndUploadPDF(blob);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setUploadStatus({
        success: false,
        message: `Error generating PDF: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const getCustomerAgency = async () => {
    const response = await axiosRubyClient.get(
      `api/invoices/customer_agency?invoice_id=${invoiceId}`,
      accessToken
        ? {
            headers: {
              authorization: accessToken,
            },
          }
        : undefined,
    );

    return response.data;
  };

  const getUploadUrlAndUploadPDF = async (blob: Blob) => {
    try {
      setIsUploading(true);

      // First, get the presigned S3 URL using axiosClient to ensure BASE_URL_V3 is used
      const uploadUrlResponse = await axiosRubyClient.get(
        `api/invoices/upload_url?invoice_id=${invoiceId}`,
        accessToken
          ? {
              headers: {
                authorization: accessToken,
              },
            }
          : undefined,
      );
      const uploadData = uploadUrlResponse.data as UploadUrlResponse;

      setUploadDetails(uploadData);
      console.log('Got upload URL:', uploadData);

      // Now upload the PDF directly to the presigned URL
      await uploadToS3(blob, uploadData);
    } catch (error) {
      console.error('Error in upload process:', error);

      // More detailed error message based on type of error
      let errorMessage = 'Unknown error occurred';

      if (axios.isAxiosError(error)) {
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          errorMessage = `Server error: ${error.response.status} - ${error.response.data?.message || error.message}`;
        } else if (error.request) {
          // The request was made but no response was received
          errorMessage =
            'No response received from server. The server might be unavailable.';
        } else {
          // Something happened in setting up the request
          errorMessage = `Request setup error: ${error.message}`;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setUploadStatus({
        success: false,
        message: `Error uploading PDF: ${errorMessage}`,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const uploadToS3 = async (blob: Blob, uploadData: UploadUrlResponse) => {
    try {
      // Upload directly to the presigned S3 URL
      // Note: We use regular axios here, not axiosClient, since this is going to S3 not our API
      await axios.put(uploadData.url, blob, {
        headers: {
          'Content-Type': uploadData.content_type,
        },
      });

      setUploadStatus({
        success: true,
        message: `PDF generated and uploaded successfully to S3 bucket: ${uploadData.bucket}!`,
      });
    } catch (error) {
      console.error('Error uploading to S3:', error);

      let errorMessage = 'Unknown error occurred during S3 upload';

      if (axios.isAxiosError(error)) {
        if (error.response) {
          errorMessage = `S3 error: ${error.response.status} - ${error.message}`;
        } else if (error.request) {
          errorMessage =
            'No response received from S3. The connection might be unavailable.';
        } else {
          errorMessage = `S3 request setup error: ${error.message}`;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      throw new Error(errorMessage);
    }
  };

  const handleRetryUpload = async () => {
    if (pdfBlob) {
      await getUploadUrlAndUploadPDF(pdfBlob);
    } else {
      await generatePDF();
    }
  };

  // For loading or error states in fetching invoice data
  if (loadingInvoice) {
    return (
      <div className="pdf-generation-container" style={{ padding: '20px' }}>
        <h1>Invoice PDF Generation</h1>
        <div style={{ marginTop: '20px' }}>
          <LoadingState text="Loading invoice data..." />
        </div>
      </div>
    );
  }

  if (invoiceError) {
    return (
      <div className="pdf-generation-container" style={{ padding: '20px' }}>
        <h1>Invoice PDF Generation</h1>
        <div
          style={{
            marginTop: '20px',
            padding: '15px',
            backgroundColor: '#ffe6e6',
            borderRadius: '4px',
            border: '1px solid #cc9999',
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Error</div>
          <div>Failed to load invoice data: {invoiceError.message}</div>
        </div>
      </div>
    );
  }

  if (!invoiceId || !invoiceData?.listStripeInvoiceDetail?.invoice) {
    return (
      <div className="pdf-generation-container" style={{ padding: '20px' }}>
        <h1>Invoice PDF Generation</h1>
        <div
          style={{
            marginTop: '20px',
            padding: '15px',
            backgroundColor: '#ffe6e6',
            borderRadius: '4px',
            border: '1px solid #cc9999',
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            No Invoice Data
          </div>
          <div>Please provide a valid invoice ID to generate the PDF.</div>
        </div>
      </div>
    );
  }

  const isLoading = isGenerating || isUploading;
  const invoice = invoiceData.listStripeInvoiceDetail.invoice;
  const invoiceNumber = invoice.number || invoice.invoiceUid;

  return (
    <div className="pdf-generation-container" style={{ padding: '20px' }}>
      <h1>Invoice PDF Generation</h1>
      {isPublicAccess && (
        <div
          style={{ marginTop: '10px', marginBottom: '20px', color: '#4785FF' }}
        >
          Accessing invoice via public link with token authentication
        </div>
      )}
      <div
        style={{ marginTop: '10px', marginBottom: '20px', fontSize: '16px' }}
      >
        <strong>Invoice:</strong> {invoiceNumber} | <strong>Customer:</strong>{' '}
        {invoice.customer.name}
      </div>

      {isLoading ? (
        <div style={{ marginTop: '20px' }}>
          {isGenerating
            ? 'Generating Invoice PDF...'
            : 'Uploading PDF to S3...'}
        </div>
      ) : (
        <div>
          {uploadStatus.success !== undefined && (
            <div
              style={{
                marginTop: '20px',
                padding: '15px',
                backgroundColor: uploadStatus.success ? '#e6ffe6' : '#ffe6e6',
                borderRadius: '4px',
                border: uploadStatus.success
                  ? '1px solid #99cc99'
                  : '1px solid #cc9999',
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                {uploadStatus.success ? 'Success' : 'Error'}
              </div>
              <div>{uploadStatus.message}</div>

              {uploadStatus.success && uploadDetails && (
                <div style={{ marginTop: '10px', fontSize: '0.9em' }}>
                  <div>
                    <strong>File Key:</strong> {uploadDetails.key}
                  </div>
                  <div>
                    <strong>Bucket:</strong> {uploadDetails.bucket}
                  </div>
                  <div>
                    <strong>Content Type:</strong> {uploadDetails.content_type}
                  </div>
                  <div>
                    <strong>Privacy:</strong>{' '}
                    {uploadDetails.is_private ? 'Private' : 'Public'}
                  </div>
                </div>
              )}

              {!uploadStatus.success && (
                <button
                  onClick={handleRetryUpload}
                  style={{
                    marginTop: '10px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Retry
                </button>
              )}
            </div>
          )}

          {pdfBlob && invoiceData?.listStripeInvoiceDetail?.invoice && (
            <div style={{ marginTop: '20px' }}>
              <PDFDownloadLink
                document={
                  <InvoiceDetailsPdf
                    invoice={invoiceData.listStripeInvoiceDetail.invoice}
                    invoiceItems={
                      invoiceData.listStripeInvoiceDetail.invoiceItems
                    }
                    currentAgency={currentAgency}
                    refiningInvoiceNumber={false}
                  />
                }
                fileName={`invoice-${invoiceNumber}.pdf`}
                style={{
                  backgroundColor: '#4785FF',
                  color: 'white',
                  padding: '10px 15px',
                  borderRadius: '4px',
                  textDecoration: 'none',
                  display: 'inline-block',
                }}
              >
                {({ blob, url, loading, error }) =>
                  loading
                    ? 'Preparing invoice document...'
                    : 'Download Invoice PDF'
                }
              </PDFDownloadLink>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PDFGeneration;
